import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/da23pcrk8r5te5mqsfng";

const problemTypes = ["网站打不开", "加载慢", "换源失败", "收藏不同步", "搜索无结果", "登录问题", "其他"];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");
    setStatus("loading");

    const form = e.currentTarget;
    const data = new FormData(form);

    // 反垃圾蜜罐：一旦被自动填充即静默终止，不给机器人明确信号
    const honey = (data.get("website_alt") as string)?.trim() || "";
    if (honey) {
      setStatus("success");
      form.reset();
      return;
    }
    // 空的蜜罐字段不进入请求体
    data.delete("website_alt");

    const email = (data.get("email") as string)?.trim() || "";
    const message = (data.get("message") as string)?.trim() || "";
    if (!email || !message) {
      setFormError("请填写联系邮箱和问题描述后再提交。");
      setStatus("idle");
      return;
    }
    if (message.length > 500) {
      setFormError("问题描述不能超过 500 字，请精简后重试。");
      setStatus("idle");
      return;
    }

    // 组装真实表单数据，删除空的蜜罐字段
    if (honey) data.delete("website_alt");
    data.delete("website_alt");

    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam|form data is spam/i.test(serverMsg);

      if (!res.ok || !parsed || parsed.code !== "OK" || isSpam) {
        setFormError(serverMsg || "提交失败，请稍后重试。");
        setStatus("error");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setFormError("网络异常，提交未成功，请稍后再试。");
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="scroll-mt-20 bg-background-50 py-14 md:py-20">
      <div className="container-x grid gap-10 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-sm font-medium text-primary-700">
            <i className="ri-customer-service-2-line"></i>
            联系我们
          </span>
          <h2 className="mt-4 font-heading text-2xl text-foreground-950 md:text-3xl">
            还有问题？告诉我们
          </h2>
          <p className="mt-3 text-base leading-relaxed text-foreground-700">
            如果上面的指南和常见问题没有覆盖到你的情况，欢迎通过下方表单反馈。请尽量描述清楚设备、网络和具体现象，方便我们更快帮你定位。
          </p>

          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-4 rounded-xl border border-background-200 bg-background-100/60 p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary-100 text-secondary-900">
                <i className="ri-mail-line text-xl"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-950">意见反馈</p>
                <p className="text-sm text-foreground-700">support@bibimanhua.example</p>
              </div>
            </div>
            <div className="flex items-center gap-4 rounded-xl border border-background-200 bg-background-100/60 p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-accent-900">
                <i className="ri-time-line text-xl"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-950">响应时间</p>
                <p className="text-sm text-foreground-700">工作日 9:00 - 21:00，通常在 24 小时内回复</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <form
            data-readdy-form="笔笔漫画网帮助反馈表单"
            onSubmit={handleSubmit}
            className="rounded-2xl border border-background-200 bg-background-100/60 p-6 md:p-8"
            noValidate
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-900">
                  称呼
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  placeholder="怎么称呼你（选填）"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                />
              </div>
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-900">
                  联系邮箱 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="用于接收处理结果"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="type" className="mb-1.5 block text-sm font-medium text-foreground-900">
                问题类型
              </label>
              <select
                id="type"
                name="type"
                className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
              >
                {problemTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-5">
              <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-900">
                问题描述 <span className="text-primary-600">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder="请描述你遇到的具体问题，如设备型号、网络环境、出现时间、操作步骤等…"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
              ></textarea>
              <p className="mt-1 text-right text-xs text-foreground-600">最多 500 字</p>
            </div>

            {/* 反垃圾字段 */}
            <div className="honey">
              <label htmlFor="website_alt">Website</label>
              <input
                type="text"
                id="website_alt"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </div>

            {status === "success" && (
              <div className="mt-5 flex items-start gap-3 rounded-lg bg-secondary-100 p-4 text-sm text-secondary-900">
                <i className="ri-checkbox-circle-line mt-0.5 text-lg"></i>
                <p>提交成功，感谢你的反馈！我们会尽快查看并回复你。</p>
              </div>
            )}

            {status === "error" && (
              <div className="mt-5 flex items-start gap-3 rounded-lg bg-primary-100 p-4 text-sm text-primary-900">
                <i className="ri-error-warning-line mt-0.5 text-lg"></i>
                <p>{formError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-base font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  提交中…
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  提交反馈
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}